import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
  FiUser,
  FiCreditCard,
  FiBell,
  FiShield,
  FiCheck,
  FiAlertCircle,
  FiLock,
  FiKey,
  FiCopy,
  FiTrash2,
  FiRefreshCw,
} from "react-icons/fi";

// Get current app ID from environment
const CURRENT_APP_ID =
  window.ENV?.REACT_APP_APP_ID || process.env.REACT_APP_APP_ID || "payments";

function Settings() {
  const { user, logout, getAuthConfig } = useAuth();
  const [activeTab, setActiveTab] = useState("profile");
  const [message, setMessage] = useState(null);

  // MFA Status state
  const [mfaStatus, setMfaStatus] = useState(null);
  const [mfaLoading, setMfaLoading] = useState(false);

  // MFA Setup state
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [mfaSetupData, setMfaSetupData] = useState(null);
  const [mfaSetupCode, setMfaSetupCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);

  // MFA Disable state
  const [showMfaDisable, setShowMfaDisable] = useState(false);
  const [mfaDisablePassword, setMfaDisablePassword] = useState("");
  const [mfaDisableCode, setMfaDisableCode] = useState("");

  // Regenerate backup codes state
  const [showRegenerateCodes, setShowRegenerateCodes] = useState(false);
  const [regenerateCode, setRegenerateCode] = useState("");
  const [regeneratedCodes, setRegeneratedCodes] = useState(null);

  // App MFA Settings state (for current app only)
  const [currentAppMfa, setCurrentAppMfa] = useState(null);
  const [loadingAppMfa, setLoadingAppMfa] = useState(false);
  const [updatingAppMfa, setUpdatingAppMfa] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Delete account state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const { url: AUTH_SERVICE_URL, apiKey: AUTH_API_KEY } = getAuthConfig();

  const showNotification = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  // Load MFA status
  const loadMfaStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_SERVICE_URL}/mfa/status`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-API-Key": AUTH_API_KEY,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setMfaStatus(data);
      }
    } catch (error) {
      console.error("Error loading MFA status:", error);
    }
  }, [AUTH_SERVICE_URL, AUTH_API_KEY]);

  // Load app MFA settings for current app
  const loadAppMfaSettings = useCallback(async () => {
    setLoadingAppMfa(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${AUTH_SERVICE_URL}/users/applications/mfa-settings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "X-API-Key": AUTH_API_KEY,
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        const currentApp = (data.applications || []).find(
          (app) =>
            app.application_id === CURRENT_APP_ID ||
            app.application_name?.toLowerCase() === CURRENT_APP_ID.toLowerCase()
        );
        setCurrentAppMfa(currentApp || null);
      }
    } catch (error) {
      console.error("Error loading app MFA settings:", error);
    } finally {
      setLoadingAppMfa(false);
    }
  }, [AUTH_SERVICE_URL, AUTH_API_KEY]);

  // Load data when security tab is selected
  useEffect(() => {
    if (activeTab === "security") {
      loadMfaStatus();
      loadAppMfaSettings();
    }
  }, [activeTab, loadMfaStatus, loadAppMfaSettings]);

  // Setup MFA
  const handleSetupMfa = async () => {
    setMfaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_SERVICE_URL}/mfa/setup/totp`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-API-Key": AUTH_API_KEY,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setMfaSetupData(data);
        setShowMfaSetup(true);
      } else {
        showNotification("error", "Failed to start MFA setup");
      }
    } catch (error) {
      showNotification("error", "Failed to start MFA setup");
    } finally {
      setMfaLoading(false);
    }
  };

  // Verify MFA setup
  const handleVerifyMfaSetup = async () => {
    if (mfaSetupCode.length !== 6) return;
    setMfaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_SERVICE_URL}/mfa/verify/totp`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-Key": AUTH_API_KEY,
        },
        body: JSON.stringify({ code: mfaSetupCode }),
      });
      if (response.ok) {
        const data = await response.json();
        setBackupCodes(data.backupCodes || []);
        loadMfaStatus();
      } else {
        showNotification("error", "Invalid verification code");
      }
    } catch (error) {
      showNotification("error", "Failed to verify MFA");
    } finally {
      setMfaLoading(false);
    }
  };

  // Disable MFA
  const handleDisableMfa = async () => {
    if (!mfaDisablePassword || !mfaDisableCode) return;
    setMfaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_SERVICE_URL}/mfa/disable`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-Key": AUTH_API_KEY,
        },
        body: JSON.stringify({
          password: mfaDisablePassword,
          code: mfaDisableCode,
        }),
      });
      if (response.ok) {
        setShowMfaDisable(false);
        setMfaDisablePassword("");
        setMfaDisableCode("");
        loadMfaStatus();
        showNotification("success", "MFA has been disabled");
      } else {
        showNotification("error", "Failed to disable MFA");
      }
    } catch (error) {
      showNotification("error", "Failed to disable MFA");
    } finally {
      setMfaLoading(false);
    }
  };

  // Regenerate backup codes
  const handleRegenerateBackupCodes = async () => {
    if (regenerateCode.length !== 6) return;
    setMfaLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${AUTH_SERVICE_URL}/mfa/backup-codes/regenerate`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-API-Key": AUTH_API_KEY,
          },
          body: JSON.stringify({ code: regenerateCode }),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setRegeneratedCodes(data.backupCodes || []);
        loadMfaStatus();
      } else {
        showNotification("error", "Failed to regenerate backup codes");
      }
    } catch (error) {
      showNotification("error", "Failed to regenerate backup codes");
    } finally {
      setMfaLoading(false);
    }
  };

  // Update app MFA preference
  const updateAppMfaPreference = async (applicationId, preference) => {
    setUpdatingAppMfa(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${AUTH_SERVICE_URL}/users/applications/${applicationId}/mfa-preference`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-API-Key": AUTH_API_KEY,
          },
          body: JSON.stringify({ mfa_preference: preference }),
        }
      );
      if (response.ok) {
        setCurrentAppMfa((prev) =>
          prev ? { ...prev, mfa_preference: preference } : prev
        );
        showNotification("success", "MFA preference updated");
      } else {
        showNotification("error", "Failed to update preference");
      }
    } catch (error) {
      showNotification("error", "Failed to update preference");
    } finally {
      setUpdatingAppMfa(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      showNotification("error", "Passwords do not match");
      return;
    }
    if (newPassword.length < 8) {
      showNotification("error", "Password must be at least 8 characters");
      return;
    }

    setPasswordLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_SERVICE_URL}/users/profile`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-API-Key": AUTH_API_KEY,
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });
      if (response.ok) {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        showNotification("success", "Password updated successfully");
      } else {
        const data = await response.json();
        showNotification("error", data.message || "Failed to change password");
      }
    } catch (error) {
      showNotification("error", "Failed to change password");
    } finally {
      setPasswordLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== "DELETE") return;
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_SERVICE_URL}/users/me`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "X-API-Key": AUTH_API_KEY,
        },
      });
      if (response.ok) {
        showNotification("success", "Account deleted successfully");
        setTimeout(() => logout(), 2000);
      } else {
        showNotification("error", "Failed to delete account");
      }
    } catch (error) {
      showNotification("error", "Failed to delete account");
    } finally {
      setDeleteLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification("success", "Copied to clipboard");
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">
          Manage your account and payment preferences
        </p>
      </div>

      <div className="settings-layout">
        {/* Sidebar */}
        <div className="card settings-nav" style={{ height: "fit-content" }}>
          <nav>
            <button
              className={`nav-item settings-nav-item ${
                activeTab === "profile" ? "active" : ""
              }`}
              onClick={() => setActiveTab("profile")}
            >
              <FiUser /> Profile
            </button>
            <button
              className={`nav-item settings-nav-item ${
                activeTab === "payment" ? "active" : ""
              }`}
              onClick={() => setActiveTab("payment")}
            >
              <FiCreditCard /> Payment Methods
            </button>
            <button
              className={`nav-item settings-nav-item ${
                activeTab === "notifications" ? "active" : ""
              }`}
              onClick={() => setActiveTab("notifications")}
            >
              <FiBell /> Notifications
            </button>
            <button
              className={`nav-item settings-nav-item ${
                activeTab === "security" ? "active" : ""
              }`}
              onClick={() => setActiveTab("security")}
            >
              <FiShield /> Security
            </button>
          </nav>
        </div>

        {/* Content */}
        <div className="card">
          {activeTab === "profile" && (
            <div>
              <h2 className="card-title">Profile Information</h2>
              <p className="text-muted mb-4">
                Update your personal information
              </p>

              <div className="grid grid-2">
                <div className="form-group">
                  <label className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-input"
                    defaultValue={user?.first_name || user?.firstName || ""}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-input"
                    defaultValue={user?.last_name || user?.lastName || ""}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  defaultValue={user?.email || ""}
                  disabled
                />
                <small className="text-muted">
                  Contact support to change your email
                </small>
              </div>

              <button className="btn btn-primary">Save Changes</button>
            </div>
          )}

          {activeTab === "payment" && (
            <div>
              <h2 className="card-title">Payment Methods</h2>
              <p className="text-muted mb-4">
                Manage your saved payment methods
              </p>

              <div className="empty-state">
                <FiCreditCard size={48} className="empty-state-icon" />
                <h3 className="empty-state-title">No Payment Methods</h3>
                <p className="empty-state-text">
                  Payment methods are added automatically when you make a
                  purchase
                </p>
              </div>
            </div>
          )}

          {activeTab === "notifications" && (
            <div>
              <h2 className="card-title">Notification Preferences</h2>
              <p className="text-muted mb-4">Control how we contact you</p>

              <div className="form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                >
                  <input type="checkbox" defaultChecked />
                  <span>Payment receipts</span>
                </label>
              </div>

              <div className="form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                >
                  <input type="checkbox" defaultChecked />
                  <span>Subscription renewals</span>
                </label>
              </div>

              <div className="form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                >
                  <input type="checkbox" defaultChecked />
                  <span>Trial expiration reminders</span>
                </label>
              </div>

              <div className="form-group">
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    cursor: "pointer",
                  }}
                >
                  <input type="checkbox" />
                  <span>Promotional offers</span>
                </label>
              </div>

              <button className="btn btn-primary">Save Preferences</button>
            </div>
          )}

          {activeTab === "security" && (
            <div>
              <h2 className="card-title">Security Settings</h2>
              <p className="text-muted mb-4">
                Manage your security preferences
              </p>

              {message && (
                <div
                  className={`alert alert-${
                    message.type === "error" ? "danger" : message.type
                  }`}
                  style={{ marginBottom: "24px" }}
                >
                  {message.type === "success" ? (
                    <FiCheck style={{ marginRight: "8px" }} />
                  ) : (
                    <FiAlertCircle style={{ marginRight: "8px" }} />
                  )}
                  {message.text}
                </div>
              )}

              {/* MFA Section */}
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                  marginBottom: "24px",
                }}
              >
                <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>
                  <FiShield style={{ marginRight: "8px" }} />
                  Two-Factor Authentication
                </h3>

                {mfaStatus?.enabled ? (
                  <div>
                    <div
                      className="alert alert-success"
                      style={{ marginBottom: "16px" }}
                    >
                      <FiCheck style={{ marginRight: "8px" }} />
                      MFA is enabled
                      <span
                        style={{
                          marginLeft: "8px",
                          padding: "2px 8px",
                          backgroundColor: "#28a745",
                          color: "white",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        Active
                      </span>
                    </div>
                    <p className="text-muted" style={{ marginBottom: "16px" }}>
                      Your account is protected with two-factor authentication.
                    </p>

                    {mfaStatus.backupCodesRemaining !== undefined && (
                      <p style={{ marginBottom: "16px" }}>
                        <strong>Backup codes remaining:</strong>{" "}
                        {mfaStatus.backupCodesRemaining}
                      </p>
                    )}

                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowRegenerateCodes(true)}
                      >
                        <FiKey style={{ marginRight: "4px" }} />
                        Regenerate Codes
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={() => setShowMfaDisable(true)}
                      >
                        Disable MFA
                      </button>
                    </div>

                    {/* App MFA Section - Inline (only when MFA is enabled) */}
                    {loadingAppMfa ? (
                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid var(--border-color)",
                        }}
                      >
                        <div
                          className="spinner"
                          style={{ width: "20px", height: "20px" }}
                        ></div>
                      </div>
                    ) : currentAppMfa &&
                      currentAppMfa.mfa_enabled &&
                      !currentAppMfa.mfa_required ? (
                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid var(--border-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          flexWrap: "wrap",
                          gap: "12px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <span
                            className="text-muted"
                            style={{ fontSize: "14px" }}
                          >
                            MFA for this app:
                          </span>
                          <select
                            className="form-input"
                            style={{ width: "auto", padding: "4px 8px" }}
                            value={
                              currentAppMfa.mfa_preference === "enabled"
                                ? "enabled"
                                : "disabled"
                            }
                            onChange={(e) =>
                              updateAppMfaPreference(
                                currentAppMfa.application_id,
                                e.target.value === "disabled"
                                  ? "inherit"
                                  : "enabled"
                              )
                            }
                            disabled={updatingAppMfa}
                          >
                            <option value="disabled">Skip MFA</option>
                            <option value="enabled">Enable MFA</option>
                          </select>
                        </div>
                        <span
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#17a2b8",
                            color: "white",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          Optional
                        </span>
                      </div>
                    ) : currentAppMfa && currentAppMfa.mfa_required ? (
                      <div
                        style={{
                          marginTop: "20px",
                          paddingTop: "16px",
                          borderTop: "1px solid var(--border-color)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span
                          className="text-muted"
                          style={{ fontSize: "14px" }}
                        >
                          MFA is required for this application
                        </span>
                        <span
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#ffc107",
                            color: "#212529",
                            borderRadius: "4px",
                            fontSize: "12px",
                          }}
                        >
                          Required
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div>
                    <div
                      className="alert alert-warning"
                      style={{ marginBottom: "16px" }}
                    >
                      <FiLock style={{ marginRight: "8px" }} />
                      MFA is not enabled
                    </div>
                    <p className="text-muted" style={{ marginBottom: "16px" }}>
                      Add an extra layer of security to your account by enabling
                      two-factor authentication.
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={handleSetupMfa}
                      disabled={mfaLoading}
                    >
                      <FiShield style={{ marginRight: "4px" }} />
                      {mfaLoading ? "Setting up..." : "Enable MFA"}
                    </button>
                  </div>
                )}
              </div>

              {/* Password Change Section */}
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "var(--bg-secondary)",
                  borderRadius: "8px",
                  marginBottom: "24px",
                }}
              >
                <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>
                  <FiLock style={{ marginRight: "8px" }} />
                  Change Password
                </h3>

                <div className="form-group">
                  <label className="form-label">Current Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <small className="text-muted">Minimum 8 characters</small>
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </div>

                <button
                  className="btn btn-primary"
                  onClick={handleChangePassword}
                  disabled={passwordLoading || !currentPassword || !newPassword}
                >
                  {passwordLoading ? "Updating..." : "Update Password"}
                </button>
              </div>

              {/* Delete Account Section */}
              <div
                style={{
                  padding: "20px",
                  border: "1px solid #dc3545",
                  borderRadius: "8px",
                }}
              >
                <h3
                  style={{
                    fontSize: "18px",
                    marginBottom: "16px",
                    color: "#dc3545",
                  }}
                >
                  <FiTrash2 style={{ marginRight: "8px" }} />
                  Delete Account
                </h3>
                <div
                  className="alert alert-danger"
                  style={{ marginBottom: "16px" }}
                >
                  This action is permanent and cannot be undone. All your data
                  will be permanently deleted.
                </div>
                <button
                  className="btn btn-danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <FiTrash2 style={{ marginRight: "4px" }} />
                  Delete My Account
                </button>
              </div>

              {/* MFA Setup Modal */}
              {showMfaSetup && (
                <div className="modal-overlay">
                  <div className="modal" style={{ maxWidth: "500px" }}>
                    <h3 style={{ marginBottom: "16px" }}>
                      {backupCodes ? "MFA Enabled Successfully!" : "Set Up MFA"}
                    </h3>

                    {backupCodes ? (
                      <div>
                        <div
                          className="alert alert-success"
                          style={{ marginBottom: "16px" }}
                        >
                          <FiCheck style={{ marginRight: "8px" }} />
                          MFA has been enabled on your account!
                        </div>
                        <h4 style={{ marginBottom: "8px" }}>
                          Save Your Backup Codes
                        </h4>
                        <p
                          className="text-muted"
                          style={{ marginBottom: "16px" }}
                        >
                          Store these codes in a safe place. You can use them to
                          access your account if you lose your authenticator
                          device.
                        </p>
                        <div
                          style={{
                            backgroundColor: "#1a1a2e",
                            padding: "16px",
                            borderRadius: "8px",
                            marginBottom: "16px",
                            fontFamily: "monospace",
                            color: "#fff",
                          }}
                        >
                          {backupCodes.map((code, i) => (
                            <div key={i}>{code}</div>
                          ))}
                        </div>
                        <button
                          className="btn btn-secondary"
                          onClick={() =>
                            copyToClipboard(backupCodes.join("\n"))
                          }
                          style={{ marginRight: "8px" }}
                        >
                          <FiCopy style={{ marginRight: "4px" }} />
                          Copy Codes
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setShowMfaSetup(false);
                            setBackupCodes(null);
                            setMfaSetupData(null);
                            setMfaSetupCode("");
                          }}
                        >
                          Done
                        </button>
                      </div>
                    ) : mfaSetupData ? (
                      <div>
                        <p style={{ marginBottom: "16px" }}>
                          Scan this QR code with your authenticator app:
                        </p>
                        {mfaSetupData.qrCode && (
                          <div
                            style={{
                              textAlign: "center",
                              marginBottom: "16px",
                            }}
                          >
                            <img
                              src={mfaSetupData.qrCode}
                              alt="QR Code"
                              style={{ maxWidth: "200px" }}
                            />
                          </div>
                        )}
                        <p
                          className="text-muted"
                          style={{ marginBottom: "8px", fontSize: "14px" }}
                        >
                          Can't scan? Enter this code manually:
                        </p>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "16px",
                          }}
                        >
                          <code
                            style={{
                              backgroundColor: "#1a1a2e",
                              color: "#fff",
                              padding: "8px 12px",
                              borderRadius: "4px",
                              flex: 1,
                              wordBreak: "break-all",
                            }}
                          >
                            {mfaSetupData.secret}
                          </code>
                          <button
                            className="btn btn-secondary"
                            onClick={() => copyToClipboard(mfaSetupData.secret)}
                            style={{ padding: "8px" }}
                          >
                            <FiCopy />
                          </button>
                        </div>
                        <div className="form-group">
                          <label className="form-label">
                            Enter the 6-digit code from your app:
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            maxLength={6}
                            value={mfaSetupCode}
                            onChange={(e) =>
                              setMfaSetupCode(e.target.value.replace(/\D/g, ""))
                            }
                            placeholder="000000"
                            style={{
                              textAlign: "center",
                              fontSize: "20px",
                              letterSpacing: "4px",
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowMfaSetup(false);
                              setMfaSetupData(null);
                              setMfaSetupCode("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleVerifyMfaSetup}
                            disabled={mfaLoading || mfaSetupCode.length !== 6}
                          >
                            {mfaLoading ? "Verifying..." : "Verify & Enable"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", padding: "20px" }}>
                        <div className="spinner"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* MFA Disable Modal */}
              {showMfaDisable && (
                <div className="modal-overlay">
                  <div className="modal" style={{ maxWidth: "400px" }}>
                    <h3 style={{ marginBottom: "16px" }}>Disable MFA</h3>
                    <div
                      className="alert alert-warning"
                      style={{ marginBottom: "16px" }}
                    >
                      Disabling MFA will make your account less secure.
                    </div>
                    <div className="form-group">
                      <label className="form-label">Your Password</label>
                      <input
                        type="password"
                        className="form-input"
                        value={mfaDisablePassword}
                        onChange={(e) => setMfaDisablePassword(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        MFA Code or Backup Code
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={mfaDisableCode}
                        onChange={(e) => setMfaDisableCode(e.target.value)}
                        placeholder="Enter code"
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowMfaDisable(false);
                          setMfaDisablePassword("");
                          setMfaDisableCode("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={handleDisableMfa}
                        disabled={
                          mfaLoading || !mfaDisablePassword || !mfaDisableCode
                        }
                      >
                        {mfaLoading ? "Disabling..." : "Disable MFA"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Regenerate Backup Codes Modal */}
              {showRegenerateCodes && (
                <div className="modal-overlay">
                  <div className="modal" style={{ maxWidth: "450px" }}>
                    <h3 style={{ marginBottom: "16px" }}>
                      <FiKey style={{ marginRight: "8px" }} />
                      {regeneratedCodes
                        ? "New Backup Codes"
                        : "Regenerate Backup Codes"}
                    </h3>

                    {regeneratedCodes ? (
                      <div>
                        <div
                          className="alert alert-success"
                          style={{ marginBottom: "16px" }}
                        >
                          New backup codes have been generated!
                        </div>
                        <p
                          className="text-muted"
                          style={{ marginBottom: "16px" }}
                        >
                          Store these codes in a safe place. Previous codes are
                          now invalid.
                        </p>
                        <div
                          style={{
                            backgroundColor: "#1a1a2e",
                            padding: "16px",
                            borderRadius: "8px",
                            marginBottom: "16px",
                            fontFamily: "monospace",
                            color: "#fff",
                          }}
                        >
                          {regeneratedCodes.map((code, i) => (
                            <div key={i}>{code}</div>
                          ))}
                        </div>
                        <button
                          className="btn btn-secondary"
                          onClick={() =>
                            copyToClipboard(regeneratedCodes.join("\n"))
                          }
                          style={{ marginRight: "8px" }}
                        >
                          <FiCopy style={{ marginRight: "4px" }} />
                          Copy Codes
                        </button>
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            setShowRegenerateCodes(false);
                            setRegeneratedCodes(null);
                            setRegenerateCode("");
                          }}
                        >
                          Done
                        </button>
                      </div>
                    ) : (
                      <div>
                        <div
                          className="alert alert-warning"
                          style={{ marginBottom: "16px" }}
                        >
                          <FiRefreshCw style={{ marginRight: "8px" }} />
                          Regenerating backup codes will invalidate all previous
                          codes.
                        </div>
                        <div className="form-group">
                          <label className="form-label">
                            Enter your current MFA code to confirm
                          </label>
                          <input
                            type="text"
                            className="form-input"
                            maxLength={6}
                            value={regenerateCode}
                            onChange={(e) =>
                              setRegenerateCode(
                                e.target.value.replace(/\D/g, "")
                              )
                            }
                            placeholder="000000"
                          />
                          <small className="text-muted">
                            Enter 6-digit code from your authenticator app
                          </small>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setShowRegenerateCodes(false);
                              setRegenerateCode("");
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn btn-primary"
                            onClick={handleRegenerateBackupCodes}
                            disabled={mfaLoading || regenerateCode.length !== 6}
                          >
                            <FiRefreshCw style={{ marginRight: "4px" }} />
                            {mfaLoading ? "Regenerating..." : "Regenerate"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Delete Account Modal */}
              {showDeleteModal && (
                <div className="modal-overlay">
                  <div className="modal" style={{ maxWidth: "400px" }}>
                    <h3 style={{ marginBottom: "16px", color: "#dc3545" }}>
                      Confirm Account Deletion
                    </h3>
                    <p>
                      <strong>
                        Are you sure you want to delete your account?
                      </strong>
                    </p>
                    <p className="text-muted" style={{ marginBottom: "16px" }}>
                      This action cannot be undone. All your data will be
                      permanently deleted.
                    </p>
                    <div className="form-group">
                      <label className="form-label">
                        Type DELETE to confirm
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={deleteConfirmation}
                        onChange={(e) => setDeleteConfirmation(e.target.value)}
                        placeholder="DELETE"
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        gap: "8px",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn btn-secondary"
                        onClick={() => {
                          setShowDeleteModal(false);
                          setDeleteConfirmation("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn btn-danger"
                        onClick={handleDeleteAccount}
                        disabled={
                          deleteConfirmation !== "DELETE" || deleteLoading
                        }
                      >
                        <FiTrash2 style={{ marginRight: "4px" }} />
                        {deleteLoading ? "Deleting..." : "Delete Account"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Settings;
